import type { ErrorComponentProps } from '@tanstack/react-router'
import { Card } from '../components/primitives/Card'
import { Icon } from '../components/primitives/Icon'
import { Button } from '../components/primitives/Button'

/**
 * Crash boundary rendered when a route component throws during render
 * (wired as `defaultErrorComponent` on both router factories). Warm,
 * calm, paperlike — consistent with NotFoundPage.
 *
 * Both actions are full page loads (`window.location`), not router
 * navigations — the router tree just crashed, so the escape hatch must
 * not depend on it.
 */
export function ErrorPage({ error }: ErrorComponentProps): JSX.Element {
  const detail = error instanceof Error ? error.message : String(error)

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '24px 16px',
        background: 'var(--paper-sunken)',
      }}
    >
      <Card padding={48} data-testid="error-page-card" style={{ maxWidth: 480, width: '100%' }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: 16,
          }}
        >
          <div aria-hidden="true" style={{ color: 'var(--ink-3)', lineHeight: 1 }}>
            <Icon name="error" size={48} />
          </div>

          <h1
            style={{
              fontFamily: 'var(--font-serif)',
              fontSize: 24,
              fontWeight: 400,
              margin: 0,
              color: 'var(--ink)',
              lineHeight: 1.3,
            }}
          >
            Something went wrong on our side.
          </h1>

          <p
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 14,
              color: 'var(--ink-2)',
              maxWidth: 360,
              margin: 0,
              lineHeight: 1.6,
            }}
          >
            An unexpected error stopped this page. Your data is safe — reloading usually
            fixes it.
          </p>

          {detail ? (
            <p
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 11,
                color: 'var(--ink-3)',
                background: 'var(--paper-sunken)',
                border: '1px solid var(--line)',
                borderRadius: 4,
                padding: '4px 8px',
                margin: 0,
                maxWidth: '100%',
                overflowWrap: 'break-word',
              }}
              data-testid="error-page-detail"
            >
              {detail}
            </p>
          ) : null}

          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
              justifyContent: 'center',
              marginTop: 8,
            }}
          >
            <Button
              variant="primary"
              size="md"
              onClick={() => {
                window.location.reload()
              }}
              data-testid="error-page-reload"
            >
              Reload page
            </Button>

            <Button
              variant="secondary"
              size="md"
              onClick={() => {
                window.location.assign('/')
              }}
              data-testid="error-page-home"
            >
              Back to home
            </Button>
          </div>
        </div>
      </Card>
    </div>
  )
}
